import { useEffect, useState } from 'react';
import { api, type HallOfFameWinner } from '../api.ts';

/**
 * Every champion, as a wall of album covers.
 *
 * The covers are the point — a collage of forty sleeves says "we have been
 * doing this a while" in a way a list of titles does not — so the tiles are
 * square, tight, and carry no text until you point at one. YouTube gives a
 * 16:9 thumbnail and Spotify a square sleeve; both are cropped to the same
 * square so the grid stays a grid.
 *
 * A winner with no artwork still gets a tile, lettered from its title, rather
 * than a hole in the wall. That case should now be rare: covers are recorded
 * with the archive, and an old Spotify champion whose cover was never recorded
 * gets it back from Spotify's public oEmbed endpoint a few at a time. See the
 * server's services/artwork.ts.
 *
 * Only contested games are here. A solo game crowned nothing — one person
 * submitted everything, so nothing was beaten — and its song does not belong
 * on the same wall as one the room voted for.
 */
function Tile({ winner }: { winner: HallOfFameWinner }) {
  const [failed, setFailed] = useState(false);
  const when = new Date(winner.endedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
  });
  const label = `${winner.title}${winner.artist ? ` — ${winner.artist}` : ''} · won ${winner.gameName}, ${when}, submitted by ${winner.submitterName}`;

  return (
    <a
      href={winner.sourceUrl}
      target="_blank"
      rel="noreferrer"
      title={label}
      aria-label={label}
      className="group relative block aspect-square overflow-hidden rounded-md border border-border bg-surface-raised transition-transform hover:z-10 hover:scale-[1.06] focus-visible:z-10"
    >
      {winner.artworkUrl && !failed ? (
        <img
          src={winner.artworkUrl}
          alt=""
          loading="lazy"
          /* Spotify's CDN is happier with no referrer than with a private
             hostname it has never seen, and the tile needs nothing from it. */
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          className="size-full object-cover"
        />
      ) : (
        <span className="flex size-full items-center justify-center text-lg font-semibold text-text-faint">
          {winner.title.slice(0, 2).toUpperCase()}
        </span>
      )}
      {/* Only on hover or keyboard focus: a caption on every tile would turn
          the collage back into the list it is trying not to be. */}
      <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-bg/85 p-1 text-[10px] leading-tight opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        <span className="block truncate font-medium">{winner.title}</span>
        <span className="block truncate text-text-faint">{winner.submitterName}</span>
      </span>
    </a>
  );
}

export function HallOfFame() {
  const [winners, setWinners] = useState<HallOfFameWinner[] | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    void api
      .hallOfFame()
      .then(({ winners: found }) => setWinners(found))
      .catch(() => setWinners([]));
  }, []);

  // Nothing to show until a game has been played to a champion, and an empty
  // frame on the home page is worse than no frame at all.
  if (!winners || winners.length === 0) return null;

  const FIRST_ROWS = 16;
  const shown = expanded ? winners : winners.slice(0, FIRST_ROWS);

  return (
    <section className="mb-4 rounded-xl border border-border bg-surface p-4 shadow-[var(--shadow)]">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium">
          Hall of fame <span className="text-text-faint">({winners.length})</span>
        </h2>
        <p className="text-xs text-text-faint">Every champion, newest first.</p>
      </div>

      <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12">
        {shown.map((winner) => (
          <Tile key={winner.gameId} winner={winner} />
        ))}
      </div>

      {winners.length > FIRST_ROWS && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className="mt-3 text-xs text-text-muted underline-offset-2 hover:text-text hover:underline"
        >
          {expanded ? 'Show fewer' : `Show all ${winners.length}`}
        </button>
      )}
    </section>
  );
}
